import {
	DEFAULT_ENABLED_ASSESSMENT_CONFIGURATION,
	DEFAULT_PROCTORING_OPTION,
	enforceConfiguration,
} from "@verity/proctoring-option";
import { ProctoringOptionProfileV2 } from "@verity/proctoring-option/types";
import { useCallback, useEffect, useMemo } from "react";
import { coreToast } from "../../core";
import i18nPackage from "../../i18n/i18n";
import { useEditQuizToast } from "../context/EditQuizContext";
import { PublishLocation } from "../edit-quiz-modals/SaveProfileModal";
import { EditQuizPageProps } from "../editQuiz";
import { UseEditQuizStateReturn } from "./state/useEditQuizState";

type UseModalActionProps = Pick<UseEditQuizStateReturn, "profile" | "setting"> &
	Pick<
		EditQuizPageProps,
		| "onSaveProfileChange"
		| "setProctoringOption"
		| "proctoringAssessmentConfiguration"
		| "proctoringOption"
		| "quizInfo"
		| "currentProfileId"
		| "setUploadedProfilesMap"
	>;

/**Handles logic related to profiles. */
export default function useEditQuizProfile({
	onSaveProfileChange,
	setProctoringOption,
	proctoringOption,
	proctoringAssessmentConfiguration,
	quizInfo,
	currentProfileId,
	setUploadedProfilesMap,
	...props
}: UseModalActionProps) {
	const toastContainerId = useEditQuizToast();
	const toastOptions = useMemo(
		() => (toastContainerId ? { containerId: toastContainerId } : undefined),
		[toastContainerId],
	);
	const {
		initCustomConfigState,
		loadProfile,
		saveNewProfile,
		hideSaveProfileModal,
		fetchAndFilterProfiles,
		getProfile,
		updateProfile,
		deleteProfile,
	} = props.profile.action;
	const { activeProfile, profileMap } = props.profile.state;
	const { loading, verityInstituteSettings, optionRelatedInstituteSettings, enabledProctoringOptions } =
		props.setting.state;

	useEffect(() => {
		const isPresetProfile = activeProfile?.type === "preset";
		onSaveProfileChange?.({ hide: isPresetProfile || loading });
	}, [activeProfile?.type, loading, onSaveProfileChange]);

	useEffect(() => {
		if (loading) return;
		initCustomConfigState();
	}, [initCustomConfigState, loading]);

	useEffect(() => {
		setUploadedProfilesMap(profileMap);
	}, [profileMap, setUploadedProfilesMap]);

	const handleSelectProfile = useCallback(
		(profileId: number | undefined) => {
			if (!verityInstituteSettings || !proctoringAssessmentConfiguration) return;
			const selectedProfile = loadProfile(profileId);

			// Reset if no profile is chosen (custom config)
			const newProctoringOptions = selectedProfile ? selectedProfile?.proctoringOption : DEFAULT_PROCTORING_OPTION;

			setProctoringOption(
				enforceConfiguration(
					{ ...DEFAULT_ENABLED_ASSESSMENT_CONFIGURATION, ...proctoringAssessmentConfiguration },
					{ ...proctoringOption, ...newProctoringOptions },
					verityInstituteSettings,
					optionRelatedInstituteSettings,
					enabledProctoringOptions,
				).proctoringOptions,
			);
		},
		[
			enabledProctoringOptions,
			loadProfile,
			optionRelatedInstituteSettings,
			proctoringAssessmentConfiguration,
			proctoringOption,
			setProctoringOption,
			verityInstituteSettings,
		],
	);

	const handleSaveProfile = useCallback(
		async (profileName: string, publishLocation?: PublishLocation) => {
			if (!verityInstituteSettings || !publishLocation || !proctoringOption) return;

			try {
				const newProfile = await saveNewProfile({
					proctoringOption,
					profileName,
					publishLocation,
					lmsCourseId: quizInfo.courseId,
				});
				void fetchAndFilterProfiles(verityInstituteSettings);

				// the proctoringOptions in newProfile are non-null-ified, so we set the proctoringOption so the deep equal works (in case the current proctoring options still have null values).
				setProctoringOption(newProfile?.proctoringOption);
				coreToast.success(i18nPackage.t("editQuizPage.saveProfileModalTSX.success"), toastOptions);
			} catch (e) {
				console.error(e);
				coreToast.error(i18nPackage.t("editQuizPage.saveProfileModalTSX.error"), toastOptions);
			} finally {
				hideSaveProfileModal();
			}
		},
		[
			fetchAndFilterProfiles,
			hideSaveProfileModal,
			proctoringOption,
			quizInfo.courseId,
			saveNewProfile,
			setProctoringOption,
			verityInstituteSettings,
			toastOptions,
		],
	);

	const handleUpdateProfile = useCallback(async () => {
		if (!verityInstituteSettings || !proctoringOption || !currentProfileId) return;

		const profileName = getProfile(currentProfileId)?.name ?? undefined;

		try {
			await updateProfile(currentProfileId, proctoringOption, profileName);
			void fetchAndFilterProfiles(verityInstituteSettings);
			coreToast.success(i18nPackage.t("editQuizPage.saveProfileModalTSX.updateSuccess"), toastOptions);
		} catch (e) {
			console.error(e);
		} finally {
			hideSaveProfileModal();
		}
	}, [
		currentProfileId,
		fetchAndFilterProfiles,
		getProfile,
		hideSaveProfileModal,
		proctoringOption,
		updateProfile,
		verityInstituteSettings,
		toastOptions,
	]);

	const handleDeleteProfile = useCallback(
		async (profile: ProctoringOptionProfileV2) => {
			if (!verityInstituteSettings) return;

			try {
				await deleteProfile(profile);
				void fetchAndFilterProfiles(verityInstituteSettings);
				coreToast.success(i18nPackage.t("editQuizPage.deleteProfileModal.success"), toastOptions);
			} catch (e) {
				console.error(e);
				coreToast.error(i18nPackage.t("editQuizPage.deleteProfileModal.error"), toastOptions);
			}
		},
		[deleteProfile, fetchAndFilterProfiles, verityInstituteSettings, toastOptions],
	);

	return {
		handleSelectProfile,
		handleSaveProfile,
		handleUpdateProfile,
		handleDeleteProfile,
	};
}
